// frontend/src/components/layout/AppShell.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TokenStorage } from '../../utils/api';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';
import { useSync } from '../../contexts/SyncContext';
import NotificationBell, { clearNotificationCache } from './NotificationBell';
import TrialBanner from '../common/TrialBanner';
import FeedbackWidget from './FeedbackWidget';
import { Toast } from '../ui';
import api from '../../utils/api';
import {
  Home,
  BarChart3,
  Calendar,
  Link2,
  History,
  MessageSquare,
  BarChart2,
  Sparkles,
  AlertCircle,
  Bell,
  Briefcase,
  GitBranch,
  Building2,
  Settings,
  CreditCard,
  Menu,
  X,
  LogOut,
  RefreshCw,
  Check,
  Shield,
} from 'lucide-react';

// Module-level admin cache — survives navigation, cleared on logout
let cachedIsAdmin = null;
export function clearAdminCache() { cachedIsAdmin = null; }

export default function AppShell({ children, onLogout }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { entities, activeFilter, setActiveFilter, hasEntities } = useBusinessEntity();
  const {
    syncing, syncStatus, triggerSync, lastSyncResult, lastSyncAt,
    pendingMatchCount, pendingSuggestionCount
  } = useSync();

  // Admin check (cached at module level to avoid re-fetching on every navigation)
  const [isAdmin, setIsAdmin] = useState(cachedIsAdmin === true);
  useEffect(() => {
    if (cachedIsAdmin !== null) return; // already fetched this session
    api.get('/api/user/me')
      .then(res => {
        const admin = !!res.data.user?.is_admin;
        cachedIsAdmin = admin;
        if (admin) setIsAdmin(true);
      })
      .catch(() => { cachedIsAdmin = false; });
  }, []);

  // Toast + sync done state
  const [showToast, setShowToast] = useState(false);
  const [syncDone, setSyncDone] = useState(false);

  // Show toast when sync completes — include existing pending items
  useEffect(() => {
    if (!lastSyncResult) return;
    setShowToast(true);
    const timer = setTimeout(() => setShowToast(false), 8000);
    return () => clearTimeout(timer);
  }, [lastSyncResult]);

  // Brief "done" checkmark after sync completes
  useEffect(() => {
    if (!lastSyncAt) return;
    setSyncDone(true);
    const timer = setTimeout(() => setSyncDone(false), 3000);
    return () => clearTimeout(timer);
  }, [lastSyncAt]);

  const isActive = (path) => loc.pathname === path;
  const isActivePrefix = (prefix) => loc.pathname.startsWith(prefix);

  // Format "last sync" time
  const formatLastSync = () => {
    if (!lastSyncAt) return null;
    const diffMs = Date.now() - lastSyncAt.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    return `${Math.floor(diffMin / 60)}h ago`;
  };

  // Pages where the entity switcher should appear
  const showSwitcher = hasEntities && [
    '/planner/recurring',
    '/planner/forecast',
    '/planner/matching',
    '/planner/subscriptions',
    '/planner/history',
    '/planner/debts',
    '/planner/reports',
  ].includes(loc.pathname);

  const handleLogout = async () => {
    clearNotificationCache();
    clearAdminCache();
    if (onLogout) {
      onLogout();
    } else {
      TokenStorage.removeToken();
      nav('/logged-out', { replace: true });
    }
  };

  const goTo = (path) => {
    nav(path);
    setMobileMenuOpen(false);
  };

  // --- Nav item renderer ---
  const NavItem = ({ icon: IconComp, label, path, badge, matchPrefix }) => {
    const active = matchPrefix ? isActivePrefix(path) : isActive(path);
    return (
      <button
        aria-current={active ? 'page' : undefined}
        onClick={() => goTo(path)}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full min-h-[44px] lg:min-h-0 ${
          active
            ? 'bg-sidebar-active text-white'
            : 'text-sidebar-text hover:bg-sidebar-hover'
        }`}
      >
        <IconComp className="w-5 h-5 flex-shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        {badge > 0 && (
          <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-purple-500 text-white text-[10px] font-bold leading-none px-1 shadow-sm">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>
    );
  };

  // --- Section header renderer ---
  const SectionHeader = ({ label, mobileHidden }) => (
    <div className={`mt-1 lg:mt-6 mb-0.5 lg:mb-2 px-3${mobileHidden ? ' hidden lg:block' : ''}`}>
      <div className="border-t border-sidebar-border/40 lg:hidden" />
      <span className="hidden lg:inline text-[11px] uppercase tracking-wider text-sidebar-section font-medium">
        {label}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex">
      {/* ── Mobile Header ── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-sidebar-border px-4 py-3 flex items-center justify-between">
        <button onClick={() => { nav('/dashboard'); setMobileMenuOpen(false); }} className="flex items-center gap-2">
          <img src="/STB_LOGO_V1.png" alt="Secure The Bag" className="h-8 w-auto" />
          <span className="font-display font-semibold text-sm text-warm-ink">Secure The Bag</span>
        </button>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-sidebar-hover focus:outline-none focus:ring-2 focus:ring-sidebar-active/30"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-sidebar-text" />
            ) : (
              <Menu className="w-6 h-6 text-sidebar-text" />
            )}
          </button>
        </div>
      </div>

      {/* ── Mobile Drawer Backdrop ── */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`
          fixed lg:sticky inset-y-0 left-0 z-40 lg:top-0
          w-64 lg:w-56 h-screen flex flex-col
          bg-sidebar-bg border-r border-sidebar-border
          transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Sidebar Header */}
        <div className="h-14 lg:h-auto px-4 lg:py-5 flex items-center gap-2">
          <img src="/STB_LOGO_V1.png" alt="Secure The Bag" className="h-8 w-auto flex-shrink-0 hidden lg:block" />
          <span className="font-display font-semibold text-warm-ink text-sm leading-tight hidden lg:block">
            Secure The Bag
          </span>
        </div>

        {/* Scrollable Navigation */}
        <nav className="flex-1 flex flex-col px-3 overflow-y-auto min-h-0">
          {/* HOME — hidden on mobile (logo in header navigates home) */}
          <div className="hidden lg:block">
            <SectionHeader label="Home" />
            <NavItem icon={Home} label="Home" path="/dashboard" />
          </div>

          {/* PLANNER */}
          <SectionHeader label="Planner" mobileHidden />
          <NavItem icon={BarChart3} label="8-Week Forecast" path="/planner/forecast" />
          <NavItem icon={Calendar} label="Bills & Income" path="/planner/recurring" />
          <NavItem icon={Link2} label="Sync & Match" path="/planner/matching" badge={pendingMatchCount} />
          <NavItem icon={History} label="Transaction History" path="/planner/history" />

          {/* INSIGHTS */}
          <SectionHeader label="Insights" />
          <NavItem icon={MessageSquare} label="AI Assistant" path="/planner/assistant" />
          <NavItem icon={BarChart2} label="Reports" path="/planner/reports" />

          {/* ALERTS — merged into Insights on mobile */}
          <SectionHeader label="Alerts" mobileHidden />
          <NavItem icon={Bell} label="SMS Alerts" path="/dashboard/notifications" />

          {/* MANAGE */}
          <SectionHeader label="Manage" />
          <NavItem
            icon={Sparkles}
            label="Subscriptions"
            path="/planner/subscriptions"
            badge={pendingSuggestionCount}
          />
          <NavItem icon={AlertCircle} label="Debt Payoff" path="/planner/debts" />

          {/* BUSINESS */}
          <SectionHeader label="Business" />
          <NavItem icon={Briefcase} label="Business Entities" path="/planner/business" />
          <NavItem icon={GitBranch} label="Split Rules" path="/planner/splits" />

          {/* ADMIN — only visible to admins */}
          {isAdmin && (
            <>
              <SectionHeader label="Admin" />
              <NavItem icon={Shield} label="Admin Console" path="/admin" />
            </>
          )}

          {/* Mobile: moved from bottom-pinned to save vertical space */}
          <div className="lg:hidden">
            <NavItem icon={Building2} label="Linked Accounts" path="/dashboard/banks" />
            <NavItem icon={CreditCard} label="Plan & Billing" path="/dashboard/billing" />
          </div>
        </nav>

        {/* BOTTOM pinned items — always visible, never scrolls */}
        <div className="flex-shrink-0 px-3 pb-3">
          <div className="border-t border-sidebar-border pt-3 space-y-1">
            {/* Global Sync Button */}
            <button
              onClick={() => { triggerSync(); setMobileMenuOpen(false); }}
              disabled={syncing}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full min-h-[44px] lg:min-h-0 ${
                syncing
                  ? 'bg-blue-50 text-blue-700'
                  : syncDone
                  ? 'bg-green-50 text-green-700'
                  : 'text-sidebar-text hover:bg-sidebar-hover'
              }`}
            >
              {syncing ? (
                <RefreshCw className="w-5 h-5 flex-shrink-0 animate-spin" />
              ) : syncDone ? (
                <Check className="w-5 h-5 flex-shrink-0" />
              ) : (
                <RefreshCw className="w-5 h-5 flex-shrink-0" />
              )}
              <div className="flex-1 text-left">
                <div>{syncing ? syncStatus || 'Syncing...' : syncDone ? 'Done' : 'Sync All'}</div>
                {!syncing && lastSyncAt && (
                  <div className="text-[10px] text-gray-400 font-normal">{formatLastSync()}</div>
                )}
              </div>
            </button>

            <div className="hidden lg:block"><NavItem icon={Building2} label="Linked Accounts" path="/dashboard/banks" /></div>
            <NavItem icon={Settings} label="Settings" path="/settings" />
            <div className="hidden lg:block"><NavItem icon={CreditCard} label="Plan & Billing" path="/dashboard/billing" /></div>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full mt-3 mb-[env(safe-area-inset-bottom)] text-sidebar-text hover:bg-sidebar-hover min-h-[44px] lg:min-h-0"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span>Log out</span>
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="flex-1 flex flex-col pt-16 lg:pt-0 min-w-0">
        {/* Trial Banner */}
        <TrialBanner />

        {/* Entity Switcher Bar */}
        {showSwitcher && (
          <div className="border-b border-sidebar-border bg-white px-4 sm:px-6 lg:px-8 py-2.5">
            <div className="max-w-6xl mx-auto flex items-center gap-2 overflow-x-auto">
              <span className="text-xs text-gray-400 font-medium whitespace-nowrap mr-1">View:</span>
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-3 py-2 sm:py-1.5 rounded-full text-sm sm:text-xs font-medium whitespace-nowrap transition-colors min-h-[44px] sm:min-h-0 ${
                  activeFilter === 'all'
                    ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveFilter('personal')}
                className={`px-3 py-2 sm:py-1.5 rounded-full text-sm sm:text-xs font-medium whitespace-nowrap transition-colors min-h-[44px] sm:min-h-0 ${
                  activeFilter === 'personal'
                    ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Personal
              </button>
              {entities.map(entity => (
                <button
                  key={entity.id}
                  onClick={() => setActiveFilter(entity.id)}
                  className={`px-3 py-2 sm:py-1.5 rounded-full text-sm sm:text-xs font-medium whitespace-nowrap transition-colors min-h-[44px] sm:min-h-0 ${
                    activeFilter === entity.id
                      ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {entity.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Page content */}
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 min-w-0 overflow-x-hidden">
          {children}
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-200 py-4 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-center sm:text-left">
              <div className="text-sm text-gray-500">
                &copy; 2026 Secure the Bag
              </div>
              <div className="flex items-center gap-3 sm:gap-5 text-sm flex-wrap justify-center">
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="text-gray-500 hover:text-gray-700 font-medium transition-colors inline-flex items-center min-h-[44px] sm:min-h-0"
                >
                  Support
                </a>
                <span className="text-gray-300 hidden sm:inline">|</span>
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="text-gray-500 hover:text-gray-700 font-medium transition-colors inline-flex items-center min-h-[44px] sm:min-h-0"
                >
                  Contact
                </a>
              </div>
            </div>
          </div>
        </footer>
      </main>
      {process.env.REACT_APP_ENABLE_FEEDBACK === 'true' && <FeedbackWidget />}

      {/* Sync Complete Toast */}
      {showToast && lastSyncResult && (
        <Toast
          variant="success"
          title="Sync Complete"
          description={
            `Synced ${lastSyncResult.accountsSynced} account${lastSyncResult.accountsSynced !== 1 ? 's' : ''}.` +
            (pendingMatchCount > 0 ? ` ${pendingMatchCount} match${pendingMatchCount !== 1 ? 'es' : ''} to review.` : '') +
            (pendingSuggestionCount > 0 ? ` ${pendingSuggestionCount} detected bill${pendingSuggestionCount !== 1 ? 's' : ''} pending.` : '')
          }
          actions={[
            ...(pendingMatchCount > 0
              ? [{ label: 'Review Matches', onClick: () => { nav('/planner/matching'); setShowToast(false); } }]
              : []),
            ...(pendingSuggestionCount > 0
              ? [{ label: 'Review Bills', onClick: () => { nav('/planner/subscriptions'); setShowToast(false); } }]
              : []),
          ]}
          onDismiss={() => setShowToast(false)}
        />
      )}
    </div>
  );
}
